import type { FuseNavItemType } from '@fuse/core/FuseNavigation/types/FuseNavItemType';
import { Layers, Workflow, CalendarCheck2, Settings, LayoutDashboard, HardHat, ListChecks } from 'lucide-react';

const ALL_ROLES = ['GlobalAdmin', 'OrganisationAdmin', 'Admin', 'Planner', 'FieldTech'];
const ASSETS_ROLES = ['GlobalAdmin', 'OrganisationAdmin', 'Admin', 'Planner', 'FieldTech'];
const RULES_ROLES = ['GlobalAdmin', 'OrganisationAdmin', 'Admin', 'Planner'];
// Field techs read procedures through the API (they are what a technician works through on a job),
// but the admin portal's register is a planning surface, so the nav matches RULES_ROLES.
const PROCEDURES_ROLES = ['GlobalAdmin', 'OrganisationAdmin', 'Admin', 'Planner'];
const WORK_ORDER_ROLES = ['GlobalAdmin', 'OrganisationAdmin', 'Admin', 'Planner', 'FieldTech'];
const ADMIN_ROLES = ['GlobalAdmin', 'OrganisationAdmin'];
// Planner is included: scheduling work against a maintenance team means seeing who is on it and
// what capacity they have. Writes are still Admin+ (server-side CanManageResourcing).
const RESOURCING_ROLES = ['GlobalAdmin', 'OrganisationAdmin', 'Admin', 'Planner'];
// Platform-only surface (cross-organisation tenant management) — GlobalAdmin only, never OrganisationAdmin.
const PLATFORM_ROLES = ['GlobalAdmin'];

const navigationConfig: FuseNavItemType[] = [
	{
		id: 'dashboard',
		title: 'Dashboard',
		type: 'item',
		icon: LayoutDashboard,
		url: '/dashboard',
		auth: ALL_ROLES,
	},
	{
		id: 'assets',
		title: 'Assets',
		type: 'collapse',
		icon: Layers,
		auth: ASSETS_ROLES,
		children: [
			{
				id: 'assets-register',
				title: 'Assets Register',
				type: 'item',
				url: '/assets-register',
				auth: ASSETS_ROLES,
			},
			{
				id: 'service-contracts',
				title: 'Service Contracts',
				type: 'item',
				url: '/service-contracts',
				auth: ASSETS_ROLES,
			},
			{
				id: 'documents',
				title: 'Documents',
				type: 'item',
				url: '/documents',
				auth: ASSETS_ROLES,
			},
		],
	},
	{
		id: 'procedures',
		title: 'Procedures Register',
		type: 'item',
		icon: ListChecks,
		url: '/procedures',
		auth: PROCEDURES_ROLES,
	},
	{
		id: 'automation',
		title: 'Automation',
		type: 'collapse',
		icon: Workflow,
		auth: RULES_ROLES,
		children: [
			{
				id: 'rules',
				title: 'Rules',
				type: 'item',
				url: '/rules',
				auth: RULES_ROLES,
			},
		],
	},
	{
		id: 'work-orders',
		title: 'Work Orders',
		type: 'item',
		icon: CalendarCheck2,
		url: '/work-orders',
		auth: WORK_ORDER_ROLES,
	},
	{
		id: 'resourcing',
		title: 'Resourcing',
		type: 'collapse',
		icon: HardHat,
		auth: RESOURCING_ROLES,
		children: [
			{
				id: 'resource-insights',
				title: 'Resource Insights',
				type: 'item',
				url: '/resource-insights',
				auth: RESOURCING_ROLES,
			},
			{
				id: 'resource-register',
				title: 'Resource Register',
				type: 'item',
				url: '/resources',
				auth: RESOURCING_ROLES,
			},
			{
				id: 'maintenance-teams',
				title: 'Maintenance Teams',
				type: 'item',
				url: '/maintenance-teams',
				auth: RESOURCING_ROLES,
			},
			{
				id: 'disciplines',
				title: 'Disciplines',
				type: 'item',
				url: '/disciplines',
				auth: RESOURCING_ROLES,
			},
		],
	},
	{
		id: 'administration',
		title: 'Administration',
		type: 'collapse',
		icon: Settings,
		auth: ADMIN_ROLES,
		children: [
			{
				id: 'sites',
				title: 'Sites',
				type: 'item',
				url: '/sites',
				auth: ADMIN_ROLES,
			},
			{
				id: 'areas',
				title: 'Areas',
				type: 'item',
				url: '/areas',
				auth: ADMIN_ROLES,
			},
			{
				id: 'departments',
				title: 'Departments',
				type: 'item',
				url: '/departments',
				auth: ADMIN_ROLES,
			},
			{
				id: 'users',
				title: 'Users',
				type: 'item',
				url: '/users',
				auth: ADMIN_ROLES,
			},
			{
				id: 'organisations',
				title: 'Organisations',
				type: 'item',
				url: '/organisations',
				auth: PLATFORM_ROLES,
			},
		],
	},
];

export default navigationConfig;
